import React, { useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Download } from "../../../assets/Download";
import GenericPopover from "../../../components/GenericPopover";
import { AuthContext } from "../../../context/AuthProvider";
import { EditorContext } from "../../context/EditorContext/EditorProvider";
import {
  SetWaitMessageAction,
  SET_WAIT_MESSAGE
} from "../../actions/actionTypes";
import {
  CustomMixInfo,
  InitialTrackData,
  MainStore,
  StemFidelities,
  StemFidelity
} from "../../store";
import { Button } from "../Button";
import { ExitButton } from "../Buttons/ExitButton";
import { IconButton } from "../Buttons/IconButton";
import { MasterVolumeControl } from "../Buttons/MasterVolumeControl";
import { DynamicWaveform } from "../DynamicWaveform";
import { LoadingBar } from "../LoadingBar/LoadingBar";
import { MainMenuControls } from "../MainMenuControls";
import { ProjectInfoGrid } from "../ProjectInfoGrid";
import { TransportControls } from "../TransportControls";
import { Container, RightControls } from "./styles";
import { ExpandCollapseButton } from "../Buttons/ExpandCollapseButton";
import useModal from "../../../hooks/useModal";
import { useLocation } from "react-router-dom";
import { isDownloadLimitError } from "../../../utils/downloadGate";
import { promptSignupGate } from "../../../components/SignupGatePrompt";
import { useApolloClient } from "@apollo/react-hooks";
import { CREATE_DOWNLOAD_MUTATION_WITHOUT_TEAM } from "../../../components/TrackCheckoutDetail/queries";
import { saveAs } from "file-saver";

export let Header = props => {
  const dispatch = useDispatch();

  const user = useContext(AuthContext);
  const editor = useContext(EditorContext);

  const demoPopover = useModal();

  const initialTrackData = useSelector<MainStore, InitialTrackData>(
    state => state.project.initialTrackData
  );
  const progress = useSelector<MainStore, number>(
    state => state.editorState.renderProgressPercentage
  );
  const isRendering = useSelector<MainStore, boolean>(
    state => state.editorState.isRendering
  );
  const customMixInfo = useSelector<MainStore, CustomMixInfo | null>(
    state => state.project.customMixInfo
  );
  const isCustomMix = useMemo(
    () => customMixInfo && customMixInfo.id !== null,
    [customMixInfo]
  );

  const history = useHistory();
  const { push } = history;

  const apolloClient = useApolloClient();
  const location = useLocation();

  const downloadMix = () => {
    let stemFidelity: StemFidelity = StemFidelities.SD;
    const hasPaidAccess = user.hasPaidTrackAccess(editor.initialTrack);
    const userLoggedIn = user.isUserPrivate();
    const isUserAdmin = user.isUserAdmin();
    const isContentReviewer = user.isUserContentReviewer();
    // use HD stems if the user has paid access or is an admin or content reviewer
    // we don't need lossless stems here, only for rendering variations
    if (userLoggedIn && (hasPaidAccess || isUserAdmin || isContentReviewer))
      stemFidelity = StemFidelities.HD;

    editor.requestMixdown(
      stemFidelity,
      (blob, filename) => {
        // Record the download; if the anonymous gate rejects, prompt signup
        // instead of saving the mixdown.
        apolloClient
          .mutate({
            mutation: CREATE_DOWNLOAD_MUTATION_WITHOUT_TEAM,
            variables: {
              user: user?.user?.id ?? null,
              track: editor.initialTrack?.id,
              details: filename || "editor mixdown"
            }
          })
          .then(result => {
            if (isDownloadLimitError(result)) {
              editor.close();
              promptSignupGate(history, location);
            } else {
              saveAs(blob, filename);
            }
          })
          .catch(err => {
            if (isDownloadLimitError(err)) {
              editor.close();
              promptSignupGate(history, location);
            } else {
              // Non-gate failure: still let the user have the file.
              saveAs(blob, filename);
            }
          });
        // Hide loading message
        dispatch<SetWaitMessageAction>({
          type: SET_WAIT_MESSAGE,
          id: "PERFORMING_MIXDOWN",
          role: "processing",
          message: null
        });
      },
      () => {
        dispatch<SetWaitMessageAction>({
          type: SET_WAIT_MESSAGE,
          id: "PERFORMING_MIXDOWN",
          role: "processing",
          message: "Rendering Custom Mix..."
        });
      }
    );
  };

  return (
    <Container tightLayout={editor.tightLayout}>
      <div className="player-container">
        <div className="details">
          {initialTrackData.thumbnail && (
            <img
              className="thumbnail"
              src={initialTrackData.thumbnail}
              alt="Album cover"
            />
          )}
          <div
            className="title"
            onClick={() => {
              editor.close();
              const route = isCustomMix
                ? `/project/${customMixInfo.projectId}`
                : `/browse?trackId=${initialTrackData.id}`;
              push(route);
            }}
          >
            {isCustomMix ? customMixInfo.title : initialTrackData.title}
          </div>
          <div
            className="album"
            onClick={() => {
              editor.close();
              push(`/browse?albumId=${initialTrackData.albumId}`);
            }}
          >
            <div className="album-name">
              {initialTrackData.variationTitle
                ? `| ${initialTrackData.variationTitle} `
                : ` ${initialTrackData.albumName} `}
            </div>
          </div>
        </div>
        <TransportControls />
        <DynamicWaveform height={64} />
        <ProjectInfoGrid />
        <RightControls>
          {isRendering && (
            <LoadingBar
              progress={Math.round(progress * 100)}
              title={"Rendering audio"}
              width={115}
              isLoading={isRendering}
            />
          )}
          {editor.isDemo &&
            (!user.isUserPrivate() ||
              (!user.user.paidUser && !user.user.adminUser)) && (
              <GenericPopover
                xOffset="0px"
                yOffset="60px"
                anchor="top"
                popoverState={demoPopover}
                triggerComponent={
                  <Button className="btn-demo" height="auto" px="12">
                    Demo
                  </Button>
                }
                contents={
                  <div id="demo-subscribe-popover">
                    <h4>Subscribe for access to all tracks</h4>
                    <p>
                      The Multitrack Editor is only available to subscribers and
                      license holders.
                    </p>
                    <Button
                      className="btn-subscribe"
                      onClick={() => {
                        editor.close();
                        push("/subscription");
                      }}
                    >
                      Subscribe
                    </Button>
                  </div>
                }
              />
            )}
          <MasterVolumeControl />
          {!isRendering && (
            <IconButton svgWidth="16px" svgHeight="16px" onClick={downloadMix}>
              <Download />
            </IconButton>
          )}
          <MainMenuControls />
          <ExpandCollapseButton onClick={props.onExpand} />
          <ExitButton onClick={editor.close} />
        </RightControls>
      </div>
    </Container>
  );
};
